// ─────────────────────────────────────────────────────────────────────────────
// ENUMS  (match server values exactly)
// ─────────────────────────────────────────────────────────────────────────────

/** Analytics provider — API enum (AnalyticsProvider). Only Metabase (1) exists today. */
export enum AnalyticsProvider {
  Metabase = 1,
}

/** Integration lifecycle status — API enum (IntegrationStatus). */
export enum IntegrationStatus {
  Unknown = 0,
  NotConnected = 1,
  Connected = 2,
  Disconnected = 3,
  Degraded = 4,
  Error = 5,
}

/** Dashboard mapping lifecycle status — API enum (DashboardMappingStatus). */
export enum DashboardMappingStatus {
  Unknown = 0,
  Active = 1,
  Suspended = 2,
  Stale = 3,
}

/** Analytics user status — API enum (AnalyticsUserStatus). */
export enum AnalyticsUserStatus {
  Unknown = 0,
  Active = 1,
  Pending = 2,
  Deactivated = 3,
  Error = 4,
}

// ─────────────────────────────────────────────────────────────────────────────
// API REQUEST MODELS
// Exactly match the API request body schemas from company-api.json
// ─────────────────────────────────────────────────────────────────────────────

/**
 * POST /api/Companies/{companyId}/Analytics/connect — body.analyticsUsers items.
 * Schema: CreateAnalyticsUserCommand
 */
export class CreateAnalyticsUserCommand {
  name: string = '';
  email: string = '';
}

/**
 * Base fields shared by create/update dashboard mapping commands.
 * Schema: DashboardMappingCommand
 */
export class DashboardMappingCommand {
  applicationId: number = 0;
  applicationName: string = '';
  menuName: string = '';
  dashboardId: string = '';
  sortOrder: number = 0;
  isEnabled: boolean = true;
}

/**
 * POST /api/Companies/{companyId}/Analytics/connect — request body.
 * Schema: ConnectIntegrationCommand
 */
export class ConnectIntegrationCommand {
  provider: AnalyticsProvider = AnalyticsProvider.Metabase;
  permissionGroupName: string = '';
  collectionName: string = '';
  analyticsUsers: CreateAnalyticsUserCommand[] = [];
}

/**
 * POST /api/Companies/{companyId}/Analytics/dashboards — request body.
 * Schema: CreateDashboardMappingCommand (extends DashboardMappingCommand)
 */
export class CreateDashboardMappingCommand extends DashboardMappingCommand {}

/**
 * PUT /api/Companies/{companyId}/Analytics/dashboards/{mappingId} — request body.
 */
export class UpdateDashboardMappingCommand {
  mappingId: number = 0;
  menuName: string = '';
  sortOrder: number = 0;
  isEnabled: boolean = true;
}

/** Single item in the reorder payload. */
export class DashboardMappingOrderItem {
  mappingId: number = 0;
  sortOrder: number = 0;
}

// ─────────────────────────────────────────────────────────────────────────────
// API RESPONSE MODELS
// Exactly match the API response schemas from company-api.json
// ─────────────────────────────────────────────────────────────────────────────

/**
 * GET /api/Companies/{companyId}/Analytics/integration
 * Schema: AnalyticsIntegrationResponse
 */
export class AnalyticsIntegrationResponse {
  id: number = 0;
  companyId: number = 0;
  provider: AnalyticsProvider = AnalyticsProvider.Metabase;
  permissionGroupName: string = '';
  collectionName: string = '';
  status: IntegrationStatus = IntegrationStatus.NotConnected;
  createdAt: string = '';
}

/**
 * POST /api/Companies/{companyId}/Analytics/connect
 * Schema: ConnectIntegrationResponse
 */
export class ConnectIntegrationResponse {
  message: string = '';
  jobId: string = '';
}

/**
 * GET /api/Companies/{companyId}/Analytics/provisioningstatus
 * Schema: ProvisioningStatusResponse
 */
export class ProvisioningStatusResponse {
  isInProgress: boolean = false;
  currentStep: string = '';
  progress: number = 0;
  error: string | null = null;
  completedSteps: string[] = [];
}

/**
 * GET /api/Companies/{companyId}/Analytics/dashboards
 * POST /api/Companies/{companyId}/Analytics/dashboards
 * Schema: DashboardMappingResponse
 */
export class DashboardMappingResponse {
  id: number = 0;
  companyId: number = 0;
  applicationId: number = 0;
  applicationName: string = '';
  menuName: string = '';
  dashboardId: string = '';
  sortOrder: number = 0;
  isEnabled: boolean = true;
  status: DashboardMappingStatus = DashboardMappingStatus.Active;
  permissionKey: string = '';
}

/**
 * GET /api/Companies/{companyId}/Analytics/users
 * Schema: AnalyticsUserResponse
 */
export class AnalyticsUserResponse {
  id: number = 0;
  name: string = '';
  email: string = '';
  providerUserId: number | null = null;
  status: AnalyticsUserStatus = AnalyticsUserStatus.Active;
}

/**
 * GET /api/Companies/{companyId}/Analytics/metabase-dashboards
 * Schema: MetabaseCollectionItemResponse
 */
export class MetabaseCollectionItemResponse {
  id: number = 0;
  name: string = '';
  description: string = '';
  model: string = '';
}

/**
 * GET /api/Companies/{companyId}/Analytics/embed/{dashboardId}
 * Schema: DashboardEmbedConfigResponse
 */
export class DashboardEmbedConfigResponse {
  dashboardId: string = '';
  embedUrl: string = '';
}

// ─────────────────────────────────────────────────────────────────────────────
// STATE MODELS
// UI-friendly representations used inside Angular components/services
// ─────────────────────────────────────────────────────────────────────────────

/** State model for the analytics integration card/settings page. */
export class AnalyticsIntegrationState {
  id: number = 0;
  companyId: number = 0;
  provider: AnalyticsProvider = AnalyticsProvider.Metabase;
  providerName: string = '';
  permissionGroupName: string = '';
  collectionName: string = '';
  status: IntegrationStatus = IntegrationStatus.NotConnected;
  createdAt: Date | null = null;
  isConnected: boolean = false;
}

/** State model for a single analytics user row in the users table. */
export class AnalyticsUserState {
  id: number = 0;
  name: string = '';
  email: string = '';
  providerUserId: number | null = null;
  status: AnalyticsUserStatus = AnalyticsUserStatus.Active;
}

/** State model for a dashboard mapping row in the mappings table/menu. */
export class DashboardMappingState {
  id: number = 0;
  companyId: number = 0;
  applicationId: number = 0;
  applicationName: string = '';
  menuName: string = '';
  dashboardId: string = '';
  publicUuid: string = '';
  sortOrder: number = 0;
  isEnabled: boolean = true;
  status: DashboardMappingStatus = DashboardMappingStatus.Active;
  permissionKey: string = '';
}

/** State model for the provisioning status progress indicator. */
export class ProvisioningStatusState {
  isInProgress: boolean = false;
  currentStep: string = '';
  /** Progress percentage 0–100. */
  progress: number = 0;
  error: string | null = null;
  completedSteps: string[] = [];
}

/** State model for the embedded dashboard iframe. */
export class DashboardEmbedState {
  dashboardId: string = '';
  embedUrl: string = '';
}

// ─────────────────────────────────────────────────────────────────────────────
// MAPPERS  (API Response  →  State)
// ─────────────────────────────────────────────────────────────────────────────

export function mapIntegrationResponseToState(
  r: AnalyticsIntegrationResponse,
): AnalyticsIntegrationState {
  const state = new AnalyticsIntegrationState();
  state.id = r.id;
  state.companyId = r.companyId;
  state.provider = r.provider;
  state.providerName = AnalyticsProvider[r.provider] ?? 'Unknown';
  state.permissionGroupName = r.permissionGroupName;
  state.collectionName = r.collectionName;
  state.status = r.status;
  state.createdAt = r.createdAt ? new Date(r.createdAt) : null;
  state.isConnected =
    r.status === IntegrationStatus.Connected || r.status === IntegrationStatus.Degraded;
  return state;
}

export function mapUserResponseToState(r: AnalyticsUserResponse): AnalyticsUserState {
  const state = new AnalyticsUserState();
  state.id = r.id;
  state.name = r.name;
  state.email = r.email;
  state.providerUserId = r.providerUserId;
  state.status = r.status;
  return state;
}

export function mapDashboardMappingResponseToState(
  r: DashboardMappingResponse,
): DashboardMappingState {
  const state = new DashboardMappingState();
  state.id = r.id;
  state.companyId = r.companyId;
  state.applicationId = r.applicationId;
  state.applicationName = r.applicationName;
  state.menuName = r.menuName;
  state.dashboardId = r.dashboardId;
  state.sortOrder = r.sortOrder;
  state.isEnabled = r.isEnabled;
  state.status = r.status;
  state.permissionKey = r.permissionKey;
  return state;
}

export function mapProvisioningResponseToState(
  r: ProvisioningStatusResponse,
): ProvisioningStatusState {
  const state = new ProvisioningStatusState();
  state.isInProgress = r.isInProgress;
  state.currentStep = r.currentStep;
  state.progress = r.progress;
  state.error = r.error;
  state.completedSteps = [...r.completedSteps];
  return state;
}

export function mapEmbedConfigResponseToState(
  r: DashboardEmbedConfigResponse,
): DashboardEmbedState {
  const state = new DashboardEmbedState();
  state.dashboardId = r.dashboardId;
  state.embedUrl = r.embedUrl;
  return state;
}

// ─────────────────────────────────────────────────────────────────────────────
// ALIASES
// ─────────────────────────────────────────────────────────────────────────────

/** @deprecated Use ConnectIntegrationCommand */
export type ConnectIntegrationRequest = ConnectIntegrationCommand;
